import { gql } from "@apollo/client";
import { getActionMenuColumn } from "@resource/atlas";
import { useAuthContext } from "auth/context";
import { ContentTemplateToDuplicate } from "components/CreateContentTemplateModal";
import {
  ArchiveContentTemplateFromTable,
  ArchiveContentTemplateFromTableVariables,
  ContentTemplatesPaginated_currentUserPrisma_currentOrganization_contentTemplates as ContentTemplate,
  UnarchiveContentTemplateFromTable,
  UnarchiveContentTemplateFromTableVariables,
} from "generated/schemaTypes";
import { useCallback } from "react";
import { analytics } from "react-hooks/useAnalytics";
import { CellProps } from "react-table";
import { PermissionEnum } from "utils/permissions";
import useMutation from "utils/useMutation";

export const ARCHIVE_CONTENT_TEMPLATE = gql`
  mutation ArchiveContentTemplateFromTable(
    $input: ArchiveContentTemplateInput!
  ) {
    archiveContentTemplate(input: $input) {
      success
      code
      message
      contentTemplate {
        id
        archivedAt
      }
    }
  }
`;

export const UNARCHIVE_CONTENT_TEMPLATE = gql`
  mutation UnarchiveContentTemplateFromTable(
    $input: UnarchiveContentTemplateInput!
  ) {
    unarchiveContentTemplate(input: $input) {
      success
      code
      message
      contentTemplate {
        id
        archivedAt
      }
    }
  }
`;

export const useGetContentTemplatesActionMenuColumn = ({
  setContentTemplateToDuplicate,
  afterStatusChangeAction,
}: {
  setContentTemplateToDuplicate: (value?: ContentTemplateToDuplicate) => void;
  afterStatusChangeAction(): void;
}) => {
  const { checkRolePermissions } = useAuthContext();

  const [archive] = useMutation<
    ArchiveContentTemplateFromTable,
    ArchiveContentTemplateFromTableVariables
  >(ARCHIVE_CONTENT_TEMPLATE);

  const [unarchive] = useMutation<
    UnarchiveContentTemplateFromTable,
    UnarchiveContentTemplateFromTableVariables
  >(UNARCHIVE_CONTENT_TEMPLATE);

  return useCallback(
    () =>
      getActionMenuColumn({
        getItems: ({ row }: CellProps<ContentTemplate>) => {
          const { archivedAt, id } = row.original;
          return [
            {
              _type: "item",
              children: "Duplicate",
              key: "duplicate",
              density: "compact",
              disabled: !checkRolePermissions(PermissionEnum.TEMPLATE_WRITE),
              onSelect: () => {
                analytics.track("StageTemplate Table - Duplicate Clicked");
                setContentTemplateToDuplicate(row.original);
              },
            },
            {
              _type: "item",
              children: archivedAt ? "Unarchive" : "Archive",
              key: archivedAt ? "unarchive" : "archive",
              density: "compact",
              disabled: !checkRolePermissions(PermissionEnum.TEMPLATE_WRITE),
              onSelect: async () => {
                if (archivedAt) {
                  analytics.track("StageTemplate Table - Unarchive Clicked");
                  await unarchive({
                    variables: {
                      input: {
                        contentTemplateId: id,
                      },
                    },
                    optimisticResponse: {
                      unarchiveContentTemplate: {
                        __typename: "UnarchiveContentTemplateResponse",
                        success: true,
                        code: "200",
                        message: "Content template unarchived",
                        contentTemplate: {
                          __typename: "ContentTemplate",
                          id,
                          archivedAt: null,
                        },
                      },
                    },
                  });
                  afterStatusChangeAction();
                } else {
                  analytics.track("StageTemplate Table - Archive Clicked");
                  await archive({
                    variables: {
                      input: {
                        contentTemplateId: id,
                      },
                    },
                    optimisticResponse: {
                      archiveContentTemplate: {
                        __typename: "ArchiveContentTemplateResponse",
                        success: true,
                        code: "200",
                        message: "Content template archived",
                        contentTemplate: {
                          __typename: "ContentTemplate",
                          id,
                          archivedAt: new Date(),
                        },
                      },
                    },
                  });
                  afterStatusChangeAction();
                }
              },
            },
          ];
        },
      }),
    [
      archive,
      checkRolePermissions,
      unarchive,
      afterStatusChangeAction,
      setContentTemplateToDuplicate,
    ]
  );
};

export default useGetContentTemplatesActionMenuColumn;
